/**
 * Contact component - Section épurée
 *
 * @description Invite les visiteurs à prendre contact uniquement par email.
 * Le formulaire (EmailJS + reCAPTCHA) a été retiré pour des raisons de sécurité.
 * L'adresse email est récupérée depuis les données "À propos" gérées dans le
 * dashboard admin (endpoint /about), elle reste donc modifiable sans redéploiement.
 */
import React, { useState, useEffect, useCallback } from "react";
import * as styles from "./contact.css";
import { commonClasses } from "../../../../styles/indexStyles";
import { useInView } from 'react-intersection-observer';
import { useLanguage } from "../../../../hooks";
import { apiService } from "../../../shared/services/apiService";
import { MdEmail } from "react-icons/md";
import type { AboutInfo } from '../../services';

const Contact: React.FC = (): JSX.Element | null => {
  const { t } = useLanguage();

  const [email, setEmail] = useState<string>('');

  // Animation d'apparition au scroll, alignée sur les autres sections
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.15 });

  const animationStyle: React.CSSProperties = {
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : 'translateY(20px)',
    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
  };

  // Récupère l'email depuis les données "À propos" (source admin)
  const loadEmail = useCallback(async () => {
    try {
      const response = await apiService.get('/about', true, {
        cacheTtlMs: 180000, // même cache que la section About
        useCache: true,
      });
      const data = response?.data as AboutInfo | undefined;
      if (data?.email) {
        setEmail(data.email.trim());
      }
    } catch (error) {
      // En cas d'erreur, on n'affiche tout simplement pas la section
      setEmail('');
    }
  }, []);

  useEffect(() => {
    loadEmail();
  }, [loadEmail]);

  const handleEmailClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (email) {
      window.location.href = `mailto:${email}`;
    }
  };

  // Pas d'email configuré dans l'admin : on masque la section
  if (!email) {
    return null;
  }

  return (
    <section
      id="contact"
      className={`${commonClasses.section} ${styles.section}`}
      ref={ref}
      aria-labelledby="contact-heading"
    >
      <div className={commonClasses.container} style={animationStyle}>
        <h2 id="contact-heading" className={`${commonClasses.title} ${styles.title}`}>
          <span className={`${commonClasses.highlight} ${styles.highlight}`} aria-hidden="true">08.</span>
          {t('contact.title')}
        </h2>

        <p className={styles.intro}>
          {t('contact.intro')}
        </p>

        <div className={styles.alternativeContact}>
          <a
            href={`mailto:${email}`}
            className={styles.emailLink}
            aria-label="Envoyer un email direct"
            onClick={handleEmailClick}
          >
            <MdEmail aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
            {email}
          </a>
        </div>
      </div>
    </section>
  );
};

export default Contact;
